/**
 * CopyTool Component
 * Create variants, expansions, or new editions from existing games
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  InputAdornment,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  Extension as ExpansionIcon,
  Tune as VariantIcon,
  AutoAwesome as NewEditionIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gamesAPI } from '../../services/api';
import { LANGUAGES } from '../../utils/constants';
import { StatusBadge } from '../Games/StatusBadge';

const CREATION_TYPES = {
  VARIANT: 'variant',
  EXPANSION: 'expansion',
  NEW_EDITION: 'new_edition'
};

const CREATION_TYPE_INFO = {
  [CREATION_TYPES.VARIANT]: {
    label: 'Variant',
    icon: <VariantIcon />,
    description: 'A variation of the game with different rules or scoring. Links to the original game.',
    example: 'e.g., "Catan: 5-6 Player Extension Rules"'
  },
  [CREATION_TYPES.EXPANSION]: {
    label: 'Expansion',
    icon: <ExpansionIcon />,
    description: 'An add-on that requires the base game. Links as a child of the parent game.',
    example: 'e.g., "Carcassonne: Inns & Cathedrals"'
  },
  [CREATION_TYPES.NEW_EDITION]: {
    label: 'New Edition',
    icon: <NewEditionIcon />,
    description: 'A standalone copy with no relationship to the original. Good for creating a starting point.',
    example: 'e.g., Creating "Game 2nd Edition" from "Game"'
  }
};

export const CopyTool = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [creationType, setCreationType] = useState(CREATION_TYPES.VARIANT);
  const [languageFilter, setLanguageFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [newGameCode, setNewGameCode] = useState('');
  const [newGameName, setNewGameName] = useState('');
  const [error, setError] = useState('');

  // Fetch games
  const { data: gamesData, isLoading } = useQuery({
    queryKey: ['games', { language: languageFilter, search, limit: 100 }],
    queryFn: () => gamesAPI.getAll({
      language: languageFilter || undefined,
      search: search || undefined,
      limit: 100
    }),
    enabled: true
  });

  const games = gamesData?.games || [];

  // Copy game mutation - uses the API endpoint
  const copyGameMutation = useMutation({
    mutationFn: async (data) => {
      const response = await gamesAPI.copy(data);
      return response;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries(['games']);
      navigate(`/games/${result.game.id}/edit`);
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to copy game');
    }
  });

  const handleSelectGame = (game) => {
    setSelectedGame(game);
    // Auto-generate suggested new game code and name
    if (creationType === CREATION_TYPES.VARIANT) {
      setNewGameCode(`${game.gameCode}-variant`);
      setNewGameName(`${game.gameName}: Variant`);
    } else if (creationType === CREATION_TYPES.EXPANSION) {
      setNewGameCode(`${game.gameCode}-exp`);
      setNewGameName(`${game.gameName}: Expansion`);
    } else {
      setNewGameCode(`${game.gameCode}-v2`);
      setNewGameName(`${game.gameName} (2nd Edition)`);
    }
  };

  const handleCreationTypeChange = (event, newType) => {
    if (newType) {
      setCreationType(newType);
      // Update suggested names if game is selected
      if (selectedGame) {
        if (newType === CREATION_TYPES.VARIANT) {
          setNewGameCode(`${selectedGame.gameCode}-variant`);
          setNewGameName(`${selectedGame.gameName}: Variant`);
        } else if (newType === CREATION_TYPES.EXPANSION) {
          setNewGameCode(`${selectedGame.gameCode}-exp`);
          setNewGameName(`${selectedGame.gameName}: Expansion`);
        } else {
          setNewGameCode(`${selectedGame.gameCode}-v2`);
          setNewGameName(`${selectedGame.gameName} (2nd Edition)`);
        }
      }
    }
  };

  const validateGameCode = (code) => {
    return /^[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*(\.[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*)?$/.test(code);
  };

  const handleCopyGame = () => {
    if (!selectedGame) {
      setError('Please select a source game');
      return;
    }

    if (!newGameCode.trim()) {
      setError('Please enter a new game code');
      return;
    }

    if (!validateGameCode(newGameCode)) {
      setError('Game code must contain only lowercase letters, numbers, hyphens, and colons (for expansions)');
      return;
    }

    if (!newGameName.trim()) {
      setError('Please enter a new game name');
      return;
    }

    setError('');
    copyGameMutation.mutate({
      sourceId: selectedGame.id,
      newGameCode: newGameCode.trim(),
      newGameName: newGameName.trim(),
      creationType
    });
  };

  const typeInfo = CREATION_TYPE_INFO[creationType];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CopyIcon /> Copy Tool
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create a new game based on an existing one. Choose the type of relationship between the
        source and new game.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Step 1: Select Creation Type</Typography>

        <ToggleButtonGroup
          value={creationType}
          exclusive
          onChange={handleCreationTypeChange}
          sx={{ mb: 2 }}
        >
          {Object.entries(CREATION_TYPE_INFO).map(([type, info]) => (
            <ToggleButton key={type} value={type} sx={{ px: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {info.icon}
                {info.label}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Alert severity="info" icon={typeInfo.icon}>
          <Typography variant="subtitle2">{typeInfo.label}</Typography>
          <Typography variant="body2">{typeInfo.description}</Typography>
          <Typography variant="caption" color="text.secondary">{typeInfo.example}</Typography>
        </Alert>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Step 2: Select Source Game</Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Language</InputLabel>
            <Select
              value={languageFilter}
              label="Language"
              onChange={(e) => setLanguageFilter(e.target.value)}
            >
              <MenuItem value="">All Languages</MenuItem>
              {LANGUAGES.map(lang => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Search games"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ flex: 1 }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Game Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {games.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No games found
                    </TableCell>
                  </TableRow>
                ) : (
                  games.map(game => (
                    <TableRow
                      key={game.id}
                      selected={selectedGame?.id === game.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleSelectGame(game)}
                    >
                      <TableCell>{game.gameCode}</TableCell>
                      <TableCell>{game.gameName}</TableCell>
                      <TableCell>
                        <Chip
                          label={game.language.toUpperCase()}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={game.status} />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={selectedGame?.id === game.id ? 'contained' : 'outlined'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectGame(game);
                          }}
                        >
                          {selectedGame?.id === game.id ? 'Selected' : 'Select'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {selectedGame && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Step 3: Configure New Game</Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="New Game Code"
              value={newGameCode}
              onChange={(e) => setNewGameCode(e.target.value.toLowerCase())}
              required
              error={newGameCode && !validateGameCode(newGameCode)}
              helperText={
                newGameCode && !validateGameCode(newGameCode)
                  ? 'Use lowercase letters, numbers, hyphens, and colons'
                  : 'Must be unique. Example: my-game-variant'
              }
              sx={{ flex: 1 }}
            />

            <TextField
              label="New Game Name"
              value={newGameName}
              onChange={(e) => setNewGameName(e.target.value)}
              required
              helperText="Display name for the new game"
              sx={{ flex: 1 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Summary
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Source Game:</strong> {selectedGame.gameName} ({selectedGame.gameCode})
              </Typography>
              <Typography variant="body2">
                <strong>New Game:</strong> {newGameName || '(enter name)'} ({newGameCode || '(enter code)'})
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {typeInfo.label}
              </Typography>
              <Typography variant="body2">
                <strong>Language:</strong> {selectedGame.language.toUpperCase()}
              </Typography>

              {creationType === CREATION_TYPES.VARIANT && (
                <Typography variant="body2" color="primary">
                  <strong>Relationship:</strong> Will set originalGameCode = "{selectedGame.originalGameCode || selectedGame.gameCode}"
                </Typography>
              )}

              {creationType === CREATION_TYPES.EXPANSION && (
                <Typography variant="body2" color="primary">
                  <strong>Relationship:</strong> Will set parentGameCode = "{selectedGame.parentGameCode || selectedGame.gameCode}"
                </Typography>
              )}

              {creationType === CREATION_TYPES.NEW_EDITION && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Relationship:</strong> None (standalone copy)
                </Typography>
              )}
            </Box>
          </Box>

          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<CopyIcon />}
            onClick={handleCopyGame}
            disabled={
              !newGameCode ||
              !newGameName ||
              !validateGameCode(newGameCode) ||
              copyGameMutation.isLoading
            }
          >
            {copyGameMutation.isLoading ? 'Creating...' : `Create ${typeInfo.label}`}
          </Button>
        </Paper>
      )}
    </Box>
  );
};
